import type { Slide } from '@features/powerpoint';
import {
  type GroupedRetailers,
  MAX_RETAILER_COUNT,
  retailerLabel,
  sortRetailers,
} from '@features/campaigns/subdomains/cpg';
import {
  performanceByTopRetailersSlideV2,
  type PerformanceByTopRetailersSlideV2Props,
} from './performance-by-top-retailers-slide';

const ROWS_PER_PAGE = 9;

type PerformanceByTopRetailersSlidesV2Props = Pick<
  PerformanceByTopRetailersSlideV2Props,
  'excludedColumns'
> & {
  slideMaster: string;
  groupedRetailers: GroupedRetailers<
    PerformanceByTopRetailersSlideV2Props['retailers'][number]
  >;
  overallRetailer: PerformanceByTopRetailersSlideV2Props['retailers'][number];
};

export const performanceByTopRetailersSlidesV2 = (
  props: PerformanceByTopRetailersSlidesV2Props
): Slide[] => {
  const { groupedRetailers, overallRetailer, ...restProps } = props;

  const topRetailers = groupedRetailers.topRetailers
    .toSorted(sortRetailers)
    .slice(0, MAX_RETAILER_COUNT);

  const hasOther = !!groupedRetailers.otherRetailer;

  const allRows = [
    overallRetailer,
    ...topRetailers,
    ...(hasOther ? [groupedRetailers.otherRetailer!] : []),
  ];

  const slideCount = Math.max(1, Math.ceil(allRows.length / ROWS_PER_PAGE));

  return Array.from({ length: slideCount }, (_, index) => {
    const start = index * ROWS_PER_PAGE;
    const pageRows = allRows.slice(start, start + ROWS_PER_PAGE);

    return performanceByTopRetailersSlideV2({
      ...restProps,
      page: index + 1,
      totalPages: slideCount,
      retailers: pageRows.map(retailer => ({
        ...retailer,
        name: retailerLabel(retailer.name),
      })),
    });
  });
};
